import { useState, useEffect, useRef, useCallback } from 'react';
import { Equipment } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Search,
  Sparkles,
  Loader2,
  Dumbbell,
  Award,
  ArrowRight,
  ChevronDown,
  Shield,
  Zap,
  Users,
  CheckCircle,
  Package,
  LogIn,
  X,
  History,
  Brain,
  Trophy,
  Flame,
  Gamepad2,
  Target,
  Medal,
  Menu,
} from 'lucide-react';
import { FACULTY_ROLE_LABEL } from '../types';
import * as api from '../utils/supabase/api';

interface LandingPageProps {
  onNavigateToLogin: () => void;
  onNavigateToSignup: () => void;
  onNavigateToLoginWithEquipment?: (equipmentName: string) => void;
  onNavigateToSignupWithEquipment?: (equipmentName: string) => void;
}

export function LandingPage({ onNavigateToLogin, onNavigateToSignup, onNavigateToLoginWithEquipment, onNavigateToSignupWithEquipment }: LandingPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoadingEquipment, setIsLoadingEquipment] = useState(true);
  const [searchResults, setSearchResults] = useState<Equipment[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load equipment on mount (public endpoint)
  useEffect(() => {
    loadEquipment();
    // Load recent searches from localStorage
    try {
      const saved = localStorage.getItem('sportrixai-recent-searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);

  // Click outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [equipment]);

  const loadEquipment = async () => {
    try {
      const { equipment: data } = await api.getPublicEquipment();
      setEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
    } finally {
      setIsLoadingEquipment(false);
    }
  };

  // AI-powered search
  const getAISuggestions = async (query: string) => {
    if (!query.trim() || equipment.length === 0) {
      setAiSuggestions([]);
      return;
    }
    try {
      setIsAIProcessing(true);
      const { suggestions } = await api.getPublicAIEquipmentSuggestions(query, equipment);
      setAiSuggestions(suggestions);
    } catch {
      const basic = equipment
        .filter(
          (item) =>
            item.name.toLowerCase().includes(query.toLowerCase()) ||
            item.category.toLowerCase().includes(query.toLowerCase())
        )
        .map((item) => item.name)
        .slice(0, 5);
      setAiSuggestions(basic);
    } finally {
      setIsAIProcessing(false);
    }
  };

  // Debounced search with autocomplete
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      if (!hasSearched) setShowSearchResults(false);
      setAiSuggestions([]);
      setShowAutocomplete(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => {
      const results = equipment.filter(
        (item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(results);
      setIsSearching(false);
      // Show autocomplete dropdown while typing (before pressing Enter)
      if (!hasSearched) {
        setShowAutocomplete(true);
      }
      getAISuggestions(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, equipment]);

  // Save search to recent
  const saveRecentSearch = useCallback((query: string) => {
    if (!query.trim()) return;
    setRecentSearches((prev) => {
      const updated = [query, ...prev.filter((s) => s !== query)].slice(0, 5);
      try { localStorage.setItem('sportrixai-recent-searches', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  // Execute search (Enter key or click)
  const executeSearch = useCallback((query: string) => {
    if (!query.trim()) return;
    setSearchQuery(query);
    setHasSearched(true);
    setShowSearchResults(true);
    setShowAutocomplete(false);
    setHighlightedIndex(-1);
    saveRecentSearch(query);
  }, [saveRecentSearch]);

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setHighlightedIndex(-1);
    if (!value.trim()) {
      setHasSearched(false);
      setShowSearchResults(false);
    }
  };

  // Keyboard navigation for autocomplete
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    const allSuggestions = getAutocompleteSuggestions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, allSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < allSuggestions.length) {
        executeSearch(allSuggestions[highlightedIndex].text);
      } else if (searchQuery.trim()) {
        executeSearch(searchQuery);
      }
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
      setHighlightedIndex(-1);
    }
  };

  // Get autocomplete suggestions (combines AI, equipment matches, recent searches)
  const getAutocompleteSuggestions = useCallback(() => {
    const suggestions: { text: string; type: 'ai' | 'equipment' | 'recent' | 'category' }[] = [];
    const added = new Set<string>();

    // AI suggestions first
    aiSuggestions.forEach((s) => {
      if (!added.has(s.toLowerCase())) {
        suggestions.push({ text: s, type: 'ai' });
        added.add(s.toLowerCase());
      }
    });

    // Matching equipment names
    if (searchQuery.trim()) {
      equipment
        .filter((item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.category.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 5)
        .forEach((item) => {
          if (!added.has(item.name.toLowerCase())) {
            suggestions.push({ text: item.name, type: 'equipment' });
            added.add(item.name.toLowerCase());
          }
        });
    }

    // Recent searches
    if (!searchQuery.trim()) {
      recentSearches.forEach((s) => {
        if (!added.has(s.toLowerCase())) {
          suggestions.push({ text: s, type: 'recent' });
          added.add(s.toLowerCase());
        }
      });
    }

    return suggestions.slice(0, 8);
  }, [searchQuery, aiSuggestions, equipment, recentSearches]);

  // Filter by category
  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
      setSearchQuery('');
      setShowSearchResults(false);
      setHasSearched(false);
    } else {
      setSelectedCategory(category);
      executeSearch(category);
    }
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSuggestionClick = (suggestion: string) => {
    executeSearch(suggestion);
  };

  // Clear search and reset
  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchResults(false);
    setHasSearched(false);
    setSelectedCategory(null);
    setAiSuggestions([]);
    setShowAutocomplete(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Get unique categories with counts
  const categories = equipment.reduce<Record<string, { count: number; available: number }>>((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = { count: 0, available: 0 };
    }
    acc[item.category].count += item.quantity;
    acc[item.category].available += item.available;
    return acc;
  }, {});

  const totalEquipment = equipment.reduce((sum, item) => sum + item.quantity, 0);
  const totalAvailable = equipment.reduce((sum, item) => sum + item.available, 0);
  const totalBorrowed = totalEquipment - totalAvailable;

  const categoryEmojis: Record<string, string> = {
    'basketball': '🏀', 'volleyball': '🏐', 'football': '⚽', 'soccer': '⚽',
    'tennis': '🎾', 'badminton': '🏸', 'swimming': '🏊', 'gym': '💪',
    'running': '🏃', 'baseball': '⚾', 'table tennis': '🏓', 'boxing': '🥊',
    'archery': '🏹', 'cycling': '🚴', 'fitness': '🏋️', 'track': '🏃',
  };

  const getCategoryEmoji = (category: string) => {
    const lower = category.toLowerCase();
    for (const [key, emoji] of Object.entries(categoryEmojis)) {
      if (lower.includes(key)) return emoji;
    }
    return '🏅';
  };

  const scrollToSearch = () => {
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Fixed Navigation Bar — glassmorphism with gradient accent */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/30 transition-all duration-300">
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="relative bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-xl shadow-lg shadow-blue-500/20">
                <Dumbbell className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-background animate-pulse" />
              </div>
              <div>
                <span className="font-bold text-lg tracking-tight">SPORTRIX AI</span>
                <span className="text-xs text-muted-foreground ml-1 hidden sm:inline font-medium">OHS</span>
              </div>
            </div>
            {/* Desktop nav buttons */}
            <div className="hidden sm:flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={scrollToSearch}
                className="text-sm hover:text-blue-500 transition-colors"
              >
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onNavigateToLogin}
                className="text-sm border-border/60 hover:border-blue-400/50 hover:text-blue-500"
              >
                <LogIn className="h-4 w-4 mr-1.5" />
                Sign In
              </Button>
              <Button
                size="sm"
                onClick={onNavigateToSignup}
                className="text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all"
              >
                <Flame className="h-4 w-4 mr-1" />
                Get Started
              </Button>
            </div>
            {/* Mobile hamburger button */}
            <button
              className="sm:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted/60 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown menu */}
        {isMobileMenuOpen && (
          <div className="sm:hidden border-t border-border/30 bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-3 space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { scrollToSearch(); setIsMobileMenuOpen(false); }}
                className="w-full justify-start text-sm hover:text-blue-500 transition-colors"
              >
                <Search className="h-4 w-4 mr-2" />
                Search Equipment
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onNavigateToLogin(); setIsMobileMenuOpen(false); }}
                className="w-full justify-start text-sm border-border/60 hover:border-blue-400/50 hover:text-blue-500"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
              <Button
                size="sm"
                onClick={() => { onNavigateToSignup(); setIsMobileMenuOpen(false); }}
                className="w-full justify-start text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/20"
              >
                <Flame className="h-4 w-4 mr-2" />
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* ============================================================
          HERO SECTION — Dark Sports Arena Theme
          ============================================================ */}
      <section
        ref={heroRef}
        className="landing-hero-bg relative pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32"
      >
        {/* Animated grid overlay */}
        <div className="landing-grid-overlay" />

        {/* Floating sports icons — decorative */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <span className="landing-float-1 absolute top-[15%] left-[8%] text-3xl sm:text-4xl opacity-20 select-none">🏀</span>
          <span className="landing-float-2 absolute top-[20%] right-[10%] text-3xl sm:text-4xl opacity-15 select-none">⚽</span>
          <span className="landing-float-3 absolute top-[55%] left-[5%] text-2xl sm:text-3xl opacity-15 select-none">🏐</span>
          <span className="landing-float-4 absolute top-[65%] right-[8%] text-2xl sm:text-3xl opacity-20 select-none">🏸</span>
          <span className="landing-float-5 absolute top-[35%] left-[85%] text-2xl opacity-10 select-none hidden lg:block">🎮</span>
          <span className="landing-float-6 absolute top-[75%] left-[15%] text-2xl opacity-10 select-none hidden lg:block">🏆</span>
          {/* Particle dots */}
          <div className="landing-particle" style={{ top: '20%', left: '30%', '--duration': '5s', '--delay': '0s' } as React.CSSProperties} />
          <div className="landing-particle" style={{ top: '40%', left: '70%', '--duration': '7s', '--delay': '1s' } as React.CSSProperties} />
          <div className="landing-particle" style={{ top: '60%', left: '20%', '--duration': '6s', '--delay': '2s' } as React.CSSProperties} />
          <div className="landing-particle" style={{ top: '30%', left: '80%', '--duration': '8s', '--delay': '0.5s' } as React.CSSProperties} />
          <div className="landing-particle" style={{ top: '70%', left: '60%', '--duration': '5.5s', '--delay': '1.5s' } as React.CSSProperties} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* E-Sports style badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6 sm:mb-8 animate-fade-in-up backdrop-blur-sm">
              <Gamepad2 className="h-4 w-4 text-blue-400 animate-sparkle" />
              <span className="text-sm text-blue-200/90 font-medium">
                Sports & E-Sports Equipment Hub
              </span>
              <Badge className="text-[10px] px-2 py-0 bg-blue-500/20 text-blue-300 border-blue-500/30">LIVE</Badge>
            </div>

            <h1 className="landing-hero-title text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              <span className="text-white">Gear Up. </span>
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent landing-neon-text">
                Compete.
              </span>
              <span className="block mt-2 bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                Dominate.
              </span>
            </h1>

            <p className="landing-hero-subtitle text-base sm:text-lg lg:text-xl text-slate-300/80 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              Search real-time availability, reserve gear with{' '}
              <span className="text-blue-400 font-semibold">AI-powered search</span>, 
              and manage equipment — your one-stop platform for{' '}
              <span className="text-white font-semibold">NEMSU Tandag</span> athletics.
            </p>

            {/* AI Search Bar — Glowing Sports Theme */}
            <div
              ref={searchRef}
              className="max-w-2xl mx-auto animate-fade-in-up animation-delay-300"
            >
              <div ref={autocompleteRef} className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/30 via-purple-500/20 to-cyan-500/30 rounded-2xl blur-lg opacity-60 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-500" />
                <div className={`relative bg-slate-900/80 backdrop-blur-xl border-2 border-slate-600/40 hover:border-blue-500/50 focus-within:border-blue-500/60 landing-search-glow transition-all duration-300 ${
                  showAutocomplete && getAutocompleteSuggestions().length > 0
                    ? 'rounded-t-2xl rounded-b-none border-b-0'
                    : 'rounded-2xl'
                }`}>
                  <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4">
                    <Search className="h-5 w-5 text-blue-400 flex-shrink-0" />
                    <Input
                      ref={inputRef}
                      placeholder="Search equipment... try 'basketball', 'gym', or even 'basktbol'"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      onFocus={() => {
                        if (searchQuery.trim() || recentSearches.length > 0) {
                          setShowAutocomplete(true);
                        }
                      }}
                      className="border-0 bg-transparent text-base sm:text-lg text-white placeholder:text-slate-400/60 focus-visible:ring-0 focus-visible:ring-offset-0 px-3"
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="p-1 hover:bg-slate-700 rounded-full transition-colors mr-1 flex-shrink-0"
                      >
                        <X className="h-4 w-4 text-slate-400" />
                      </button>
                    )}
                    {isAIProcessing ? (
                      <Loader2 className="h-5 w-5 text-blue-400 animate-spin flex-shrink-0" />
                    ) : searchQuery ? (
                      <Sparkles className="h-5 w-5 text-blue-400 flex-shrink-0 animate-sparkle" />
                    ) : null}
                    {searchQuery.trim() && (
                      <button
                        onClick={() => executeSearch(searchQuery)}
                        className="ml-2 p-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-500 hover:to-purple-500 transition-all flex-shrink-0 shadow-lg shadow-blue-500/25"
                        aria-label="Search"
                      >
                        <Search className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Autocomplete Dropdown — dark themed */}
                {showAutocomplete && getAutocompleteSuggestions().length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-50 bg-slate-900/95 backdrop-blur-xl border-2 border-t-0 border-slate-600/40 rounded-b-2xl shadow-2xl overflow-hidden animate-fade-in-up">
                    <div className="border-t border-slate-600/30 mx-4" />
                    <ul className="py-2">
                      {getAutocompleteSuggestions().map((suggestion, i) => (
                        <li
                          key={`${suggestion.type}-${suggestion.text}-${i}`}
                          className={`flex items-center gap-3 px-5 py-2.5 cursor-pointer transition-colors ${
                            highlightedIndex === i
                              ? 'bg-blue-500/15 text-white'
                              : 'hover:bg-slate-800/60 text-slate-200'
                          }`}
                          onClick={() => handleSuggestionClick(suggestion.text)}
                          onMouseEnter={() => setHighlightedIndex(i)}
                        >
                          {suggestion.type === 'ai' && (
                            <Sparkles className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          )}
                          {suggestion.type === 'equipment' && (
                            <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          )}
                          {suggestion.type === 'recent' && (
                            <History className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          )}
                          {suggestion.type === 'category' && (
                            <Dumbbell className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          )}
                          <span className="text-sm flex-1 text-left">
                            {suggestion.type === 'ai' ? (
                              <span className="font-medium">{suggestion.text}</span>
                            ) : (
                              suggestion.text
                            )}
                          </span>
                          {suggestion.type === 'ai' && (
                            <Badge className="text-[10px] px-1.5 py-0 flex-shrink-0 bg-blue-500/20 text-blue-300 border-blue-500/30">
                              AI
                            </Badge>
                          )}
                          {suggestion.type === 'recent' && (
                            <span className="text-xs text-slate-500 flex-shrink-0">recent</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {aiSuggestions.length > 0 && (
                      <div className="border-t border-slate-600/30 px-5 py-2 flex items-center justify-center gap-2">
                        <Brain className="h-3 w-3 text-blue-400" />
                        <span className="text-[11px] text-slate-500">
                          Powered by Deep NLP — understands typos, intent & context
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Quick search tags — sports themed */}
              {!searchQuery && !showSearchResults && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2 animate-fade-in-up animation-delay-400">
                  <span className="text-xs text-slate-500 mr-1">Popular:</span>
                  {['Basketball', 'Volleyball', 'Gym Equipment', 'Badminton', 'Soccer'].map((tag) => (
                    <button
                      key={tag}
                      onClick={() => executeSearch(tag)}
                      className="landing-sport-tag px-3 py-1.5 text-xs rounded-full border border-slate-600/50 bg-slate-800/50 text-slate-300 hover:text-blue-400 hover:border-blue-500/40 transition-all duration-200"
                    >
                      {getCategoryEmoji(tag)} {tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Search hint */}
              {!searchQuery && (
                <p className="text-xs text-slate-500/60 mt-3 animate-fade-in-up animation-delay-500">
                  Press <kbd className="px-1.5 py-0.5 rounded border border-slate-600/50 bg-slate-800/50 text-[10px] font-mono text-slate-400">Enter</kbd> to search  •  AI corrects typos automatically
                </p>
              )}
            </div>

            {/* Scroll indicator */}
            <div className="mt-12 animate-bounce-slow">
              <button
                onClick={scrollToSearch}
                className="text-slate-500/40 hover:text-blue-400 transition-colors"
                aria-label="Scroll down"
              >
                <ChevronDown className="h-6 w-6 mx-auto" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SEARCH RESULTS — Sports Scoreboard Style
          ============================================================ */}
      {showSearchResults && hasSearched && (
        <section className="py-8 sm:py-12 border-t border-border/30 bg-muted/30 landing-stripe-accent animate-fade-in-up">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Sticky search bar at top of results */}
            <div className="sticky top-16 z-40 bg-muted/60 backdrop-blur-lg pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div className="flex items-center gap-3 max-w-2xl">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-1.5 rounded-lg">
                    <Dumbbell className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-semibold text-sm hidden sm:inline">SPORTRIX AI</span>
                </div>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search equipment..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchQuery.trim()) {
                        executeSearch(searchQuery);
                      }
                    }}
                    className="pl-10 pr-10 h-10 rounded-full border-2 border-border/60 hover:border-blue-400/40 focus:border-blue-500"
                  />
                  {isAIProcessing && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
                  )}
                  {!isAIProcessing && searchQuery && (
                    <Sparkles className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-500" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="flex-shrink-0 hover:text-blue-500"
                >
                  Clear
                </Button>
              </div>

              {/* AI understanding & suggestions in results view */}
              {aiSuggestions.length > 0 && (
                <div className="mt-3 max-w-2xl">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Sparkles className="h-3 w-3 text-blue-500" />
                      {!equipment.some((e) => e.name.toLowerCase() === searchQuery.toLowerCase())
                        ? 'AI understood your search:'
                        : 'Related:'}
                    </span>
                    {aiSuggestions.map((suggestion, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="h-7 text-xs border-blue-500/30 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition-colors"
                      >
                        {suggestion}
                      </Button>
                    ))}
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-1">
                      <Brain className="h-3 w-3" /> Deep NLP
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-6 mt-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  {searchResults.length > 0 ? (
                    <>
                      <Target className="h-5 w-5 text-blue-500" />
                      {searchResults.length} item{searchResults.length !== 1 ? 's' : ''} found
                    </>
                  ) : (
                    'No results found'
                  )}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Results for "<span className="font-medium text-foreground">{searchQuery}</span>"
                </p>
              </div>
            </div>

            {searchResults.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {searchResults.map((item, index) => (
                  <Card
                    key={item.id}
                    className="group landing-card-3d border-border/40 hover:border-blue-500/30 animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <CardTitle className="text-base truncate">{item.name}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">{item.category}</p>
                        </div>
                        <Badge
                          variant={item.available > 0 ? 'default' : 'secondary'}
                          className={`flex-shrink-0 ${
                            item.available > 0
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-red-100 text-red-800 border-red-200'
                          }`}
                        >
                          {item.available > 0 ? 'Available' : 'Unavailable'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.description}
                      </p>
                      {/* Availability bar */}
                      <div>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-muted-foreground">Availability</span>
                          <span className="font-semibold">
                            {item.available}/{item.quantity}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              item.available === 0
                                ? 'bg-red-500'
                                : item.available < item.quantity * 0.3
                                  ? 'bg-amber-500'
                                  : 'bg-green-500'
                            }`}
                            style={{
                              width: `${item.quantity > 0 ? (item.available / item.quantity) * 100 : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="capitalize">Condition: {item.condition}</span>
                        {item.quantity - item.available > 0 && (
                          <span className="text-amber-600">
                            {item.quantity - item.available} borrowed
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          className="flex-1 border-blue-500/30 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:text-white group-hover:border-transparent transition-all"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (onNavigateToLoginWithEquipment) {
                              onNavigateToLoginWithEquipment(item.name);
                            } else {
                              onNavigateToLogin();
                            }
                          }}
                        >
                          <LogIn className="h-3.5 w-3.5 mr-1.5" />
                          Sign In
                        </Button>
                        <Button
                          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                          size="sm"
                          onClick={() => {
                            if (onNavigateToSignupWithEquipment) {
                              onNavigateToSignupWithEquipment(item.name);
                            } else {
                              onNavigateToSignup();
                            }
                          }}
                        >
                          <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
                          Sign Up
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Search className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">
                    No equipment matches "{searchQuery}". Try the AI suggestions above!
                  </p>
                </CardContent>
              </Card>
            )}

            {searchResults.length > 0 && (
              <div className="text-center mt-8 space-y-3">
                <Button onClick={onNavigateToLogin} size="lg" className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/20 landing-cta-pulse">
                  <Trophy className="h-4 w-4 mr-2" />
                  Sign in for Full Access
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <p className="text-xs text-muted-foreground">
                  Sign in to borrow equipment, track requests, and access AI-powered recommendations
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============================================================
          LIVE STATS SCOREBOARD
          ============================================================ */}
      <div className="landing-section-divider" />
      <section
        id="stats-section"
        data-animate
        className={`py-10 sm:py-14 landing-stats-bg transition-all duration-700 ${
          visibleSections.has('stats-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-3">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Live Inventory</span>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              {
                icon: Package,
                value: isLoadingEquipment ? '...' : totalEquipment,
                label: 'Total Equipment',
                color: 'text-blue-600 bg-blue-100',
                statColor: '#3b82f6',
              },
              {
                icon: CheckCircle,
                value: isLoadingEquipment ? '...' : totalAvailable,
                label: 'Available Now',
                color: 'text-emerald-600 bg-emerald-100',
                statColor: '#10b981',
              },
              {
                icon: Flame,
                value: isLoadingEquipment ? '...' : totalBorrowed,
                label: 'Currently Borrowed',
                color: 'text-amber-600 bg-amber-100',
                statColor: '#f59e0b',
              },
              {
                icon: Medal,
                value: isLoadingEquipment ? '...' : Object.keys(categories).length,
                label: 'Categories',
                color: 'text-purple-600 bg-purple-100',
                statColor: '#8b5cf6',
              },
            ].map((stat, i) => (
              <div
                key={i}
                className="landing-stat-card text-center bg-background rounded-xl border border-border/40 p-4 sm:p-6"
                style={{ '--stat-color': stat.statColor } as React.CSSProperties}
              >
                <div
                  className={`inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-xl ${stat.color} mb-3 transition-transform duration-300`}
                >
                  <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight ${visibleSections.has('stats-section') ? 'landing-counter' : ''}`}
                  style={{ animationDelay: `${i * 150}ms` }}
                >
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          BROWSE BY CATEGORY — Interactive Sports Grid
          ============================================================ */}
      {Object.keys(categories).length > 0 && (
        <section
          id="categories-section"
          data-animate
          className={`py-12 sm:py-20 transition-all duration-700 ${
            visibleSections.has('categories-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 landing-section-header">Browse by Sport</h2>
              <p className="text-muted-foreground max-w-lg mx-auto mt-4">
                Tap any sport to see available equipment instantly
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {Object.entries(categories).map(([category, data], i) => (
                <button
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  className={`landing-category-glow group p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-300 ${
                    selectedCategory === category
                      ? 'border-blue-500 bg-blue-500/5 shadow-lg shadow-blue-500/10'
                      : 'border-border/40 bg-background hover:border-blue-500/30'
                  }`}
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="text-2xl sm:text-3xl mb-2 group-hover:scale-110 transition-transform duration-300 inline-block">
                    {getCategoryEmoji(category)}
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base truncate">{category}</h3>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs sm:text-sm text-emerald-500 font-bold">
                      {data.available}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      / {data.count} available
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        selectedCategory === category
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                          : 'bg-gradient-to-r from-emerald-500 to-teal-500'
                      }`}
                      style={{
                        width: `${data.count > 0 ? (data.available / data.count) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          FEATURES — E-Sports Card Style with Neon Borders
          ============================================================ */}
      <div className="landing-section-divider" />
      <section
        id="features-section"
        data-animate
        className={`py-12 sm:py-20 bg-muted/20 transition-all duration-700 ${
          visibleSections.has('features-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
              <Zap className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Why SPORTRIX AI?</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 landing-section-header">
              Built for Athletes & Scholars
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto mt-4">
              Smart equipment management for NEMSU Tandag's sports community
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                icon: Sparkles,
                title: 'AI-Powered Search',
                description:
                  'Deep NLP understands typos, synonyms, and intent. Type "basktbol" and find basketballs instantly.',
                color: 'bg-purple-100 text-purple-600',
                neonColor: '#8b5cf6',
              },
              {
                icon: Target,
                title: 'Real-Time Availability',
                description:
                  'See live equipment status and availability counts. Never show up to find everything borrowed.',
                color: 'bg-blue-100 text-blue-600',
                neonColor: '#3b82f6',
              },
              {
                icon: Shield,
                title: 'Instant Reservations',
                description:
                  'Equipment is reserved immediately when you submit a request, preventing overbooking.',
                color: 'bg-emerald-100 text-emerald-600',
                neonColor: '#10b981',
              },
              {
                icon: Users,
                title: 'Role-Based Access',
                description:
                  `Tailored experiences for students, ${FACULTY_ROLE_LABEL.toLowerCase()}s, coaches, and administrators.`,
                color: 'bg-amber-100 text-amber-600',
                neonColor: '#f59e0b',
              },
              {
                icon: Award,
                title: 'Athlete Applications',
                description:
                  'Students can apply as athlete scholars with AI-assisted application review.',
                color: 'bg-rose-100 text-rose-600',
                neonColor: '#f43f5e',
              },
              {
                icon: Gamepad2,
                title: 'Sports & E-Sports Ready',
                description:
                  'Modern interface optimized for both traditional sports and esports equipment management.',
                color: 'bg-cyan-100 text-cyan-600',
                neonColor: '#06b6d4',
              },
            ].map((feature, i) => (
              <Card
                key={i}
                className="landing-feature-neon border-border/40 bg-background"
                style={{ '--neon-color': feature.neonColor } as React.CSSProperties}
              >
                <CardContent className="pt-6">
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${feature.color} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-base mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          EQUIPMENT PREVIEW — Interactive Card Grid
          ============================================================ */}
      {equipment.length > 0 && !showSearchResults && (
        <section
          id="preview-section"
          data-animate
          className={`py-12 sm:py-20 transition-all duration-700 ${
            visibleSections.has('preview-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 landing-section-header">Available Equipment</h2>
              <p className="text-muted-foreground max-w-lg mx-auto mt-4">
                Browse our latest inventory — sign in to borrow
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {equipment.slice(0, 8).map((item, index) => (
                <Card
                  key={item.id}
                  className="group landing-card-3d border-border/40 hover:border-blue-500/20"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-sm sm:text-base truncate">
                          {item.name}
                        </CardTitle>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                          {item.category}
                        </p>
                      </div>
                      <Badge
                        className={`flex-shrink-0 text-[10px] ${
                          item.available > 0
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                        }`}
                      >
                        {item.available > 0 ? `${item.available} left` : 'Out'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          item.available === 0
                            ? 'bg-red-500'
                            : item.available < item.quantity * 0.3
                              ? 'bg-amber-500'
                              : 'bg-gradient-to-r from-emerald-500 to-teal-500'
                        }`}
                        style={{
                          width: `${item.quantity > 0 ? (item.available / item.quantity) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 capitalize">
                      {item.condition} condition
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {equipment.length > 8 && (
              <div className="text-center mt-8">
                <Button variant="outline" onClick={scrollToSearch} className="group border-blue-500/30 hover:border-blue-500 hover:text-blue-600">
                  <Search className="h-4 w-4 mr-2" />
                  View All {equipment.length} Items
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============================================================
          CTA SECTION — Arena Gradient with Pulsing Button
          ============================================================ */}
      <section
        id="cta-section"
        data-animate
        className={`landing-cta-gradient relative py-16 sm:py-24 text-white overflow-hidden transition-all duration-700 ${
          visibleSections.has('cta-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-10 left-[10%] w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-[10%] w-56 h-56 bg-purple-500/10 rounded-full blur-3xl" />
          <span className="landing-float-1 absolute top-[20%] right-[15%] text-4xl opacity-10 select-none">🏆</span>
          <span className="landing-float-3 absolute bottom-[20%] left-[10%] text-3xl opacity-10 select-none">⚡</span>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6 backdrop-blur-sm">
            <Trophy className="h-4 w-4 text-yellow-400 landing-trophy-shine" />
            <span className="text-sm font-medium text-white/80">Join the Team</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            Ready to <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Get Started</span>?
          </h2>
          <p className="text-base sm:text-lg text-white/60 mb-8 max-w-2xl mx-auto">
            Sign up with your email and start borrowing equipment today. Fast, smart, and hassle-free.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={onNavigateToSignup}
              className="w-full sm:w-auto text-base px-8 group bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-400 hover:to-cyan-400 shadow-xl shadow-blue-500/30 landing-cta-pulse border-0"
            >
              <Flame className="h-4 w-4 mr-2" />
              Create Account
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={onNavigateToLogin}
              className="w-full sm:w-auto text-base px-8 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/30 backdrop-blur-sm"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================================
          FOOTER — Clean Sports Branding
          ============================================================ */}
      <footer className="py-8 sm:py-12 border-t border-border/30 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-1.5 rounded-lg">
                <Dumbbell className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold">SPORTRIX AI-OHS</span>
              <span className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} NEMSU Tandag
              </span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5" />
                Office of Head of Sports & Development
              </span>
              <span className="hidden sm:inline">&bull;</span>
              <span className="hidden sm:flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                AI-Powered Equipment Management
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
